import { CART, CART_OWNER_STORAGE_KEY, LOCAL_BOOKMARKS_STORAGE_KEY } from './constants';
import {
  getListingSizeChoices,
  normalizeSizeValue,
  resolveSelectedSizeOption,
} from './sizeHelpers';
import { resolvePurchasableStockQuantity } from './listingStock';
import { getListingSale } from './listingSale';

const isBrowser = () => typeof window !== 'undefined' && !!window.localStorage;
const LOGIN_AS_CART_RESET_PARAM = 'login_as';

const clearStoredCartData = () => {
  if (!isBrowser()) {
    return;
  }

  window.localStorage.removeItem(CART);
  window.localStorage.removeItem(LOCAL_BOOKMARKS_STORAGE_KEY);
  window.localStorage.removeItem(CART_OWNER_STORAGE_KEY);
};

const normalizeCurrencyCode = value => {
  if (value == null) {
    return null;
  }

  const asString = `${value}`.trim().toUpperCase();
  return asString.length > 0 ? asString : null;
};

const toMinorUnitAmount = rawAmount => {
  if (typeof rawAmount === 'number' && Number.isFinite(rawAmount)) {
    return Math.trunc(rawAmount);
  }

  if (typeof rawAmount === 'string') {
    const trimmed = rawAmount.trim();
    if (/^-?\d+$/.test(trimmed)) {
      return Number(trimmed);
    }
    return null;
  }

  if (!rawAmount || typeof rawAmount !== 'object') {
    return null;
  }

  if (typeof rawAmount?.value !== 'undefined') {
    const nestedValue = toMinorUnitAmount(rawAmount.value);
    if (nestedValue !== null) {
      return nestedValue;
    }
  }

  if (typeof rawAmount?.toNumber === 'function') {
    const converted = rawAmount.toNumber();
    if (typeof converted === 'number' && Number.isFinite(converted)) {
      return Math.trunc(converted);
    }
  }

  if (typeof rawAmount?.toString === 'function') {
    const asString = rawAmount.toString();
    if (/^-?\d+$/.test(`${asString}`.trim())) {
      return Number(asString);
    }
  }

  if (Number.isFinite(rawAmount.low) && Number.isFinite(rawAmount.high)) {
    const low = rawAmount.low >>> 0;
    const high = rawAmount.high | 0;
    return high * 4294967296 + low;
  }

  return null;
};

export const getPriceAmountInMinorUnits = price => {
  if (!price || typeof price !== 'object') {
    return null;
  }

  const amountFromShape = toMinorUnitAmount(price?.amount);
  if (amountFromShape !== null) {
    return amountFromShape;
  }

  const transitMoneyValue = price['~#mn'] || price['#mn'];
  if (Array.isArray(transitMoneyValue) && transitMoneyValue.length >= 1) {
    return toMinorUnitAmount(transitMoneyValue[0]);
  }

  return null;
};

const normalizeMoneyLike = rawMoney => {
  if (!rawMoney || typeof rawMoney !== 'object') {
    return null;
  }

  const amount = getPriceAmountInMinorUnits(rawMoney);
  const currencyFromTransit =
    Array.isArray(rawMoney?.['~#mn']) && rawMoney['~#mn'].length > 1 ? rawMoney['~#mn'][1] : null;
  const currency = normalizeCurrencyCode(rawMoney?.currency || currencyFromTransit);

  if (!Number.isInteger(amount) || amount <= 0 || !currency) {
    return null;
  }

  return { amount, currency };
};

const normalizeOfferPriceFromItem = (item = {}) => {
  if (!item || typeof item !== 'object') {
    return null;
  }

  const offerPriceMoney = normalizeMoneyLike(item.offerPrice);
  if (offerPriceMoney) {
    return offerPriceMoney;
  }

  const amountFromSubunits =
    toMinorUnitAmount(item.offerPriceInSubunits) ??
    toMinorUnitAmount(item.acceptedOfferInSubunits) ??
    toMinorUnitAmount(item.offerInSubunits);
  const currency = normalizeCurrencyCode(item.offerCurrency || item.acceptedOfferCurrency);

  if (Number.isInteger(amountFromSubunits) && amountFromSubunits > 0 && currency) {
    return { amount: amountFromSubunits, currency };
  }

  return null;
};

const parseAcceptedOfferPriceFromTransaction = (transaction, listingCurrency = null) => {
  const offerPriceMajor = Number(transaction?.attributes?.protectedData?.offerParams?.offerPrice);
  const amount = Number.isFinite(offerPriceMajor) && offerPriceMajor > 0 ? Math.round(offerPriceMajor * 100) : null;
  if (!Number.isInteger(amount) || amount <= 0) {
    return null;
  }

  const txCurrency = normalizeCurrencyCode(transaction?.attributes?.payinTotal?.currency);
  const currency = txCurrency || normalizeCurrencyCode(listingCurrency);
  return currency ? { amount, currency } : null;
};

const normalizeOfferContext = item => {
  const acceptedOfferId =
    normalizeOptionalString(item?.acceptedOfferId) ||
    normalizeOptionalString(item?.offerId) ||
    null;
  const offerPrice = normalizeOfferPriceFromItem(item);
  const offerCurrency = normalizeCurrencyCode(item?.offerCurrency || offerPrice?.currency);
  const offerAcceptedAt = normalizeOptionalString(item?.offerAcceptedAt || item?.acceptedOfferAt);
  const offerBuyerId = normalizeOptionalString(item?.offerBuyerId || item?.buyerId);
  const offerSellerId = normalizeOptionalString(item?.offerSellerId || item?.sellerIdFromOffer);
  const offerListingId = normalizeOptionalString(item?.offerListingId || item?.listingIdFromOffer);

  return {
    ...(acceptedOfferId ? { acceptedOfferId } : {}),
    ...(offerPrice ? { offerPrice } : {}),
    ...(offerCurrency ? { offerCurrency } : {}),
    ...(offerAcceptedAt ? { offerAcceptedAt } : {}),
    ...(offerBuyerId ? { offerBuyerId } : {}),
    ...(offerSellerId ? { offerSellerId } : {}),
    ...(offerListingId ? { offerListingId } : {}),
  };
};

export const getListingPriceAmountInMinorUnits = listing =>
  getPriceAmountInMinorUnits(listing?.attributes?.price);

const toPositiveIntegerOrNull = value => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export const clampQuantity = (value, min = 1, max = null) => {
  const normalizedMin = toPositiveIntegerOrNull(min) || 1;
  const normalizedValue = toPositiveIntegerOrNull(value) || normalizedMin;
  const normalizedMax = toPositiveIntegerOrNull(max);

  if (!normalizedMax) {
    return Math.max(normalizedValue, normalizedMin);
  }

  return Math.min(Math.max(normalizedValue, normalizedMin), normalizedMax);
};

const normalizeQuantity = value => clampQuantity(value, 1);

const normalizeOptionalString = value => {
  if (value == null) {
    return null;
  }

  const asString = `${value}`.trim();
  return asString.length > 0 ? asString : null;
};

export const isLoginAsCartResetRequest = () => {
  if (!isBrowser() || !window.location?.search) {
    return false;
  }

  try {
    const params = new URLSearchParams(window.location.search);
    const value = params.get(LOGIN_AS_CART_RESET_PARAM);
    return value === '1' || value === 'true';
  } catch (_error) {
    return false;
  }
};

export const getCartOwnerFromLocalStorage = () => {
  if (!isBrowser()) {
    return null;
  }

  return normalizeOptionalString(window.localStorage.getItem(CART_OWNER_STORAGE_KEY));
};

export const saveCartOwnerToLocalStorage = userId => {
  if (!isBrowser()) {
    return;
  }

  const ownerId = normalizeOptionalString(userId);
  if (ownerId) {
    window.localStorage.setItem(CART_OWNER_STORAGE_KEY, ownerId);
  } else {
    window.localStorage.removeItem(CART_OWNER_STORAGE_KEY);
  }
};

export const removeCartOwnerFromLocalStorage = () => {
  if (!isBrowser()) {
    return;
  }

  window.localStorage.removeItem(CART_OWNER_STORAGE_KEY);
};

const shouldIgnoreStoredCartByDefault = () => Boolean(getCartOwnerFromLocalStorage());

// Accepted-offer pricing can override the item price, but the listing's public price
// remains the default auto-purchase fallback whenever no override is available.
export const getAutoPurchaseUnitPriceBreakdown = ({
  listing,
  cartItem = null,
  activeOffer = null,
  transaction = null,
  onCurrencyMismatch = null,
} = {}) => {
  const listingPrice = normalizeMoneyLike(listing?.attributes?.price);
  const listingCurrency = normalizeCurrencyCode(listingPrice?.currency);
  const cartItemOffer = normalizeOfferPriceFromItem(cartItem);
  const activeOfferPrice = normalizeOfferPriceFromItem(activeOffer);
  const transactionOffer = parseAcceptedOfferPriceFromTransaction(transaction, listingCurrency);
  const offerPriceCandidates = [
    { price: cartItemOffer, source: 'cart-item' },
    { price: activeOfferPrice, source: 'order-data' },
    { price: transactionOffer, source: 'transaction' },
  ];
  const resolvedOfferPrice = offerPriceCandidates.find(candidate => candidate.price) || null;
  const offerPrice = resolvedOfferPrice?.price || null;

  if (!offerPrice) {
    return {
      unitPrice: listingPrice || listing?.attributes?.price || null,
      source: 'listing-price',
    };
  }

  const offerCurrency = normalizeCurrencyCode(offerPrice.currency);
  const hasCurrencyMismatch =
    listingCurrency && offerCurrency && listingCurrency !== offerCurrency;

  if (hasCurrencyMismatch) {
    if (typeof onCurrencyMismatch === 'function') {
      onCurrencyMismatch({ listingCurrency, offerCurrency });
    }
    return {
      unitPrice: listingPrice || listing?.attributes?.price || null,
      source: 'listing-price',
    };
  }

  return {
    unitPrice: offerPrice,
    source: resolvedOfferPrice?.source || 'offer-price',
  };
};

export const getAutoPurchaseUnitPrice = args =>
  getAutoPurchaseUnitPriceBreakdown(args).unitPrice;

export const getEffectiveUnitPrice = args => getAutoPurchaseUnitPrice(args);

export const getAutoPurchaseUnitPriceAmountInMinorUnits = args =>
  getPriceAmountInMinorUnits(getAutoPurchaseUnitPrice(args));

export const getEffectiveUnitPriceAmountInMinorUnits = args =>
  getAutoPurchaseUnitPriceAmountInMinorUnits(args);

const normalizeCartItem = (listingId, item = {}) => {
  const quantity = normalizeQuantity(item?.quantity);
  const sellerId = item?.sellerId || item?.authorId || item?.vendorId || null;
  const addedAt = item?.addedAt || new Date().toISOString();
  const selectedSize = normalizeOptionalString(item?.selectedSize || item?.size);
  const selectedSizeLabel = normalizeOptionalString(
    item?.selectedSizeLabel || item?.sizeLabel
  );

  return {
    listingId,
    quantity,
    addedAt,
    ...(sellerId ? { sellerId } : {}),
    ...(selectedSize ? { selectedSize } : {}),
    ...(selectedSizeLabel ? { selectedSizeLabel } : {}),
    ...normalizeOfferContext(item),
  };
};

export const normalizeCartItemsMap = cartItems => {
  if (!cartItems || typeof cartItems !== 'object') {
    return {};
  }

  return Object.keys(cartItems).reduce((acc, listingId) => {
    if (!listingId) return acc;
    acc[listingId] = normalizeCartItem(listingId, cartItems[listingId]);
    return acc;
  }, {});
};

export const getCartFromLocalStorage = ({ includeOwned = false } = {}) => {
  if (!isBrowser()) {
    return {};
  }

  if (!includeOwned && isLoginAsCartResetRequest()) {
    clearStoredCartData();
    return {};
  }

  if (!includeOwned && shouldIgnoreStoredCartByDefault()) {
    return {};
  }

  try {
    const stored = window.localStorage.getItem(CART);
    const parsed = stored ? JSON.parse(stored) : {};
    const map = parsed && typeof parsed === 'object' ? parsed : {};
    return normalizeCartItemsMap(map);
  } catch (e) {
    return {};
  }
};

export const saveCartToLocalStorage = cartItems => {
  if (!isBrowser()) {
    return;
  }

  window.localStorage.setItem(CART, JSON.stringify(cartItems || {}));
};

export const removeCartFromLocalStorage = () => {
  if (!isBrowser()) {
    return;
  }

  window.localStorage.removeItem(CART);
  removeCartOwnerFromLocalStorage();
};

export const bookmarksArrayToCartMap = (bookmarks = []) => {
  if (!Array.isArray(bookmarks)) return {};
  return bookmarks.reduce((acc, listingId) => {
    if (!listingId) return acc;
    acc[listingId] = normalizeCartItem(listingId, { quantity: 1 });
    return acc;
  }, {});
};

export const cartMapToBookmarkList = cartItems =>
  Object.keys(normalizeCartItemsMap(cartItems || {})).filter(Boolean);

export const saveBookmarksToLocalStorage = bookmarks => {
  if (!isBrowser()) return;
  window.localStorage.setItem(
    LOCAL_BOOKMARKS_STORAGE_KEY,
    JSON.stringify(Array.from(new Set(bookmarks || [])))
  );
};

export const getBookmarksFromLocalStorage = ({ includeOwned = false } = {}) => {
  if (!isBrowser()) return [];
  if (!includeOwned && isLoginAsCartResetRequest()) {
    clearStoredCartData();
    return [];
  }

  if (!includeOwned && shouldIgnoreStoredCartByDefault()) {
    return [];
  }

  try {
    const stored = window.localStorage.getItem(LOCAL_BOOKMARKS_STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

export const removeBookmarksFromLocalStorage = () => {
  if (!isBrowser()) {
    return;
  }

  window.localStorage.removeItem(LOCAL_BOOKMARKS_STORAGE_KEY);
};

export const mergeCartMaps = (...maps) => {
  return maps.reduce((acc, currentRaw) => {
    const current = normalizeCartItemsMap(currentRaw);
    Object.keys(current).forEach(listingId => {
      const existing = acc[listingId];
      const next = current[listingId];
      const nextQuantity = normalizeQuantity(next?.quantity);
      const existingQuantity = existing ? normalizeQuantity(existing?.quantity) : null;
      acc[listingId] = {
        listingId,
        quantity: existingQuantity ? Math.max(existingQuantity, nextQuantity) : nextQuantity,
        addedAt: existing?.addedAt || next?.addedAt || new Date().toISOString(),
        ...(next?.sellerId || existing?.sellerId
          ? { sellerId: next?.sellerId || existing?.sellerId }
          : {}),
        ...(next?.selectedSize || existing?.selectedSize
          ? { selectedSize: next?.selectedSize || existing?.selectedSize }
          : {}),
        ...(next?.selectedSizeLabel || existing?.selectedSizeLabel
          ? { selectedSizeLabel: next?.selectedSizeLabel || existing?.selectedSizeLabel }
          : {}),
        ...normalizeOfferContext({
          ...(existing || {}),
          ...(next || {}),
        }),
      };
    });
    return acc;
  }, {});
};

export const upsertCartItem = ({
  cartItems = {},
  listingId,
  quantity = 1,
  maxQuantity = null,
  sellerId = null,
  selectedSize = null,
  selectedSizeLabel = null,
  acceptedOfferId = null,
  offerPrice = null,
  offerCurrency = null,
  offerAcceptedAt = null,
  offerBuyerId = null,
  offerSellerId = null,
  offerListingId = null,
}) => {
  if (!listingId) {
    return cartItems;
  }

  const currentItem = cartItems?.[listingId] || {};
  const currentQuantity = toPositiveIntegerOrNull(currentItem?.quantity) || 0;
  const nextQuantity = clampQuantity(currentQuantity + normalizeQuantity(quantity), 1, maxQuantity);

  return {
    ...cartItems,
    [listingId]: {
      listingId,
      quantity: nextQuantity,
      addedAt: currentItem?.addedAt || new Date().toISOString(),
      ...(sellerId || currentItem?.sellerId ? { sellerId: sellerId || currentItem.sellerId } : {}),
      ...(selectedSize || currentItem?.selectedSize
        ? { selectedSize: selectedSize || currentItem.selectedSize }
        : {}),
      ...(selectedSizeLabel || currentItem?.selectedSizeLabel
        ? { selectedSizeLabel: selectedSizeLabel || currentItem.selectedSizeLabel }
        : {}),
      ...normalizeOfferContext({
        ...(currentItem || {}),
        acceptedOfferId,
        offerPrice,
        offerCurrency,
        offerAcceptedAt,
        offerBuyerId,
        offerSellerId,
        offerListingId,
      }),
    },
  };
};

export const setCartItemQuantity = ({ cartItems = {}, listingId, quantity = 1, maxQuantity = null }) => {
  if (!listingId) {
    return cartItems;
  }

  const currentItem = cartItems?.[listingId] || {};
  const nextQuantity = clampQuantity(quantity, 1, maxQuantity);

  return {
    ...cartItems,
    [listingId]: {
      listingId,
      quantity: nextQuantity,
      addedAt: currentItem?.addedAt || new Date().toISOString(),
      ...(currentItem?.sellerId ? { sellerId: currentItem.sellerId } : {}),
      ...(currentItem?.selectedSize ? { selectedSize: currentItem.selectedSize } : {}),
      ...(currentItem?.selectedSizeLabel ? { selectedSizeLabel: currentItem.selectedSizeLabel } : {}),
      ...normalizeOfferContext(currentItem),
    },
  };
};

export const removeCartItem = ({ cartItems = {}, listingId }) => {
  if (!listingId) {
    return cartItems;
  }

  const { [listingId]: removed, ...rest } = cartItems;
  return rest;
};

export const cartItemsArray = cartItems => {
  if (!cartItems || typeof cartItems !== 'object') {
    return [];
  }

  return Object.values(cartItems)
    .filter(item => item?.listingId)
    .map(item => ({
      listingId: item.listingId,
      quantity: normalizeQuantity(item.quantity),
      addedAt: item.addedAt,
      ...(item?.selectedSize ? { selectedSize: item.selectedSize } : {}),
      ...(item?.selectedSizeLabel ? { selectedSizeLabel: item.selectedSizeLabel } : {}),
      ...normalizeOfferContext(item),
    }));
};

export const getCartItemCount = cartItems => {
  return cartItemsArray(cartItems).reduce((sum, item) => sum + normalizeQuantity(item.quantity), 0);
};

export const resolveMaxQuantityForListing = (listing, selectedSize = null) => {
  const publicData = listing?.attributes?.publicData || {};
  const listingSizeChoices = getListingSizeChoices(publicData);
  const normalizedSelectedSize = normalizeSizeValue(selectedSize);
  const selectedSizeOrSingleSizeFallback =
    normalizedSelectedSize ||
    (listingSizeChoices.length === 1 ? normalizeSizeValue(listingSizeChoices[0]?.values) : null);

  const purchasableStock = resolvePurchasableStockQuantity(
    listing,
    selectedSizeOrSingleSizeFallback
  );

  return purchasableStock ?? 1;
};

export const extractListingDataForCart = (listing, quantity = 1, cartItem = null) => {
  if (!listing?.id?.uuid) {
    return null;
  }

  const publicData = listing?.attributes?.publicData || {};
  const cartSelectedSize = normalizeOptionalString(cartItem?.selectedSize || cartItem?.size);
  const cartSelectedSizeLabel = normalizeOptionalString(
    cartItem?.selectedSizeLabel || cartItem?.sizeLabel
  );
  const listingSizeChoices = getListingSizeChoices(publicData);
  const singleSizeFallback =
    !cartSelectedSize && listingSizeChoices.length === 1
      ? resolveSelectedSizeOption(publicData, listingSizeChoices[0]?.values)
      : null;
  const selectedSize = cartSelectedSize || singleSizeFallback?.values || null;
  const selectedSizeLabel =
    cartSelectedSizeLabel || singleSizeFallback?.label || normalizeOptionalString(publicData?.sizeLabel);

  const imageUrl =
    listing?.images?.[0]?.attributes?.variants?.['listing-card']?.url ||
    listing?.images?.[0]?.attributes?.variants?.['listing-card-2x']?.url ||
    null;
  const oldStock = resolveMaxQuantityForListing(listing, selectedSize);
  const onCurrencyMismatch = ({ listingCurrency, offerCurrency }) => {
    // eslint-disable-next-line no-console
    console.error(
      `[cart] Ignoring accepted offer price due to currency mismatch (${offerCurrency} vs ${listingCurrency})`
    );
  };
  const effectivePrice = getEffectiveUnitPrice({
    listing,
    cartItem,
    onCurrencyMismatch,
  });
  // Display only (the server prices checkout): the struck-through price while the item is on
  // sale, unless an accepted offer sets a different price
  const sale = getListingSale(listing);
  const paysSalePrice =
    !effectivePrice || effectivePrice.amount === listing?.attributes?.price?.amount;
  const originalPrice =
    sale && paysSalePrice
      ? { amount: sale.originalPrice.amount, currency: sale.originalPrice.currency }
      : null;

  return {
    listingId: listing.id.uuid,
    stockCount: clampQuantity(quantity, 1, oldStock),
    title: listing?.attributes?.title,
    price: effectivePrice || listing?.attributes?.price,
    currency: effectivePrice?.currency || listing?.attributes?.price?.currency,
    authorId: listing?.author?.id?.uuid,
    shippingPriceInSubunitsOneItem:
      listing?.attributes?.publicData?.shippingPriceInSubunitsOneItem ?? null,
    shippingPriceInSubunitsAdditionalItems:
      listing?.attributes?.publicData?.shippingPriceInSubunitsAdditionalItems ?? null,
    firstImage: imageUrl,
    ...(originalPrice ? { originalPrice } : {}),
    oldStock,
    ...(selectedSize ? { selectedSize } : {}),
    ...(selectedSizeLabel ? { selectedSizeLabel } : {}),
    ...normalizeOfferContext(cartItem),
  };
};
export const isWindowDefined = () => {
  return typeof window !== 'undefined';
};
export const getKeyFromLocalStorage = (key, type = 'string') => {
  const storedData =
    isWindowDefined() &&
    window.localStorage.getItem(key) &&
    typeof window.localStorage.getItem(key) === type &&
    JSON.parse(window.localStorage.getItem(key) || 'Tutulist');
  return storedData;
};
